import * as React from 'react';
import { styled } from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Avatar from '@mui/material/Avatar';
import IconButton, { IconButtonProps } from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Badge, Box, Breadcrumbs, Button, DialogContentText, DialogContent, DialogTitle, Dialog, Divider, Grid, Link, List, ListItem, ListItemText, Menu, MenuItem, Stack, TextField, Tooltip, Typography, DialogActions, InputAdornment, LinearProgress, FormHelperText } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface ExpandMoreProps extends IconButtonProps {
  expand: boolean;
}

interface LinearProgressWithLabelProps {
  level: 'Poor' | 'Weak' | 'Strong' | 'Very Strong';
  value: number;
}

function LinearProgressWithLabel(props: LinearProgressWithLabelProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
      <Box sx={{ width: '80%', mr: 1}}>
        <LinearProgress variant="determinate" value={props.value} color={props.level === 'Poor' ? 'error' : props.level === 'Weak' ? 'warning' : props.level === 'Strong' ? 'primary' : 'success'} />
      </Box>
      <Box sx={{ minWidth: 35, mt: 1.5 }}>
        <Typography
          variant="body2"
          sx={{ color: props.level === 'Poor' ? 'error.main' : props.level === 'Weak' ? 'warning.main' : props.level === 'Strong' ? 'primary.main' : 'success.main', fontSize: 'clamp(0.6rem, 10vw, 0.7rem)', mt: -2 }}
        >{props.level}</Typography>
      </Box>
    </Box>
  );
}

const ExpandMore = styled((props: ExpandMoreProps) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme }) => ({
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
  variants: [
    {
      props: ({ expand }) => !expand,
      style: {
        transform: 'rotate(0deg)',
      },
    },
    {
      props: ({ expand }) => !!expand,
      style: {
        transform: 'rotate(180deg)',
      },
    },
  ],
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

function stringToColor(string: string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  /* eslint-enable no-bitwise */

  return color;
}

function stringAvatar(name: string) {
  return {
    sx: {
      bgcolor: stringToColor(name),
    },
    children: `${name.split(' ')[0][0]}${name.split(' ')[1][0]}`,
  };
}

const actions = [
  'Edit',
  'Change Password'
];

export default function Account() {
  const user = localStorage.getItem('account') ? JSON.parse(localStorage.getItem('account') || '{}') : {};
  const [name, setName] = React.useState(user['name'] || '');
  const [username, setUsername] = React.useState(user['username'] || '');
  const [phone, setPhone] = React.useState(user['phone'] || '');
  const [photo, setPhoto] = React.useState(user['photo'] || null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [error, setError] = React.useState({
    'current-password': '',
    'new-password': '',
  });
  const [changePassword, setChangePassword] = React.useState(false);
  const [editable, setEditable] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState({
    'current-password-adornment': false,
    'new-password-adornment': false,
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [width, setWidth] = React.useState(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };

  }, []);

  const dynamicWidth = width > 768 ? '60%' : '100% !important';

  const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
  });

  const handleSubmit = async (event: React.SubmitEvent) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    const name = formData.get('name') as string;
    const username = formData.get('username') as string;
    const phone = formData.get('phone') as string;
    const csrfToken = await axios.get('/csrf/');
    await axios.post('/users/' + user['id'] + '/update/', {
      name: name,
      username: username,
      phone: phone,
    }, {
      headers: {
        'X-CSRFToken': csrfToken.data.csrfToken,
      },
    }).then((response) => {
      setEditable(false);
      toast.success(response.data.message);
    }).catch((error) => {
      toast.error(error.response?.data?.message);
    });
  };

  const handleUploadPhoto = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const selectedFile = file;
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhoto(event.target?.result as string);
      };
      reader.readAsDataURL(selectedFile);

      const formData = new FormData();
      formData.append('photo', selectedFile);
      const csrfToken = await axios.get('/csrf/');
      await axios.post('/users/' + user['id'] + '/upload-photo/', formData, {
        headers: {
          'X-CSRFToken': csrfToken.data.csrfToken,
          'Content-Type': 'multipart/form-data',
        },
      }).then((response) => {
        setPhoto(response.data.photo);
        toast.success(response.data.message);
      }).catch((error) => {
        setPhoto(null);
        toast.error(error.response?.data?.message);
      });
    }else{
      toast.error('Please select a photo');
      setPhoto(null);
    }
  };

  const handleClickShowPassword = (id: keyof typeof showPassword) => setShowPassword((prevState) => ({ ...prevState, [id]: !prevState[id] }));
  const handleMouseDownPassword = (id: keyof typeof showPassword) => (event: React.MouseEvent<HTMLButtonElement>) => { event.preventDefault(); };
  const handleMouseUpPassword = (id: keyof typeof showPassword) => (event: React.MouseEvent<HTMLButtonElement>) => { event.preventDefault(); };
  const definePasswordStrength = (id: string) => {
      const password = document.getElementById(id) as HTMLInputElement;
      const criteria = [
          /[a-z]/.test(password?.value),
          /[A-Z]/.test(password?.value),
          /[0-9]/.test(password?.value),
          /[^a-zA-Z0-9\s]/g.test(password?.value)
      ]

      const meetingCriteriaCount = criteria.filter(Boolean).length;

      if (password?.value?.length === 0) {
          return <></>
      }
      else if (password?.value?.length > 0 && password?.value?.length < 10) {
          return <LinearProgressWithLabel level="Poor" value={25} />
      }
      else if (password?.value?.length >= 8) {
          switch (meetingCriteriaCount) {
              case 0:
                  return <LinearProgressWithLabel level="Poor" value={25} />
              case 1:
                  return <LinearProgressWithLabel level="Weak" value={50} />
              case 2:
                  return <LinearProgressWithLabel level="Weak" value={50} />
              case 3:
                  return <LinearProgressWithLabel level="Strong" value={75} />
              case 4:
                  return <LinearProgressWithLabel level="Very Strong" value={100} />
          }
      }
  }

  return (
    <Box sx={{ display: 'flex', width: '100%', height: '100%', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
      <ToastContainer position="top-center" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme={localStorage.getItem('theme') === 'light' ? 'light' : 'dark'} />
      <Breadcrumbs aria-label="breadcrumb">
        <Link underline="hover" color="inherit" href="/">Dashboard</Link>
        <Typography sx={{ color: 'primary.main' }}>My Account</Typography>
      </Breadcrumbs>
      <Box sx={{ display: 'flex', width: '100%', height: '100%', flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 2 }}>
        <Grid container spacing={1} style={{ width: '100%', maxWidth: dynamicWidth }}>
          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
            <Card sx={{ 
              width: '100%', 
              boxShadow: 'none',  
              borderRadius: 2, 
              border: '0 !important',
              bgcolor: localStorage.getItem('theme') === 'light' && 'white !important',
              filter: (theme) =>`drop-shadow(0px 0px 1px ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.32)'})` }}
            >
              <CardHeader
                action={
                  <div>
                    <Tooltip title="Choose an action">
                      <IconButton aria-label="settings" onClick={handleClick}>
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      id="account-menu"
                      MenuListProps={{
                        'aria-labelledby': 'account-menu',
                      }}
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                    >
                      {actions.map((action) => (
                        <MenuItem 
                          key={action} 
                          sx={{ fontSize: 'clamp(0.8rem, 3vw, 0.9rem) !important;' }} 
                          onClick={() => {
                            handleClose();
                            if (action === 'Edit') {
                              setEditable(true);
                            } else {
                              setEditable(false);
                              if (action === 'Change Password') {
                                setChangePassword(true);
                              }
                            }
                          }}
                        >
                          {action}
                        </MenuItem>
                      ))}
                    </Menu>
                  </div>
                }
                title={user['name']}
                subheader={"Last logged in: " + (user['last_login'] ? user['last_login'] : 'N/A')}
                slotProps={{
                  title: {
                    component: 'span',
                    sx: {
                      fontSize: '16px',
                      fontWeight: 'bold',
                    }
                  },
                  subheader: {
                    component: 'span',
                    sx: {
                      fontSize: '12px',
                      color: 'text.secondary',
                    }
                  }
                }}
              />
              <Divider sx={{borderWidth: 2, borderColor: localStorage.getItem('theme') === 'light' ? '#F9F9FE !important' : '#192841 !important'}} />
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, padding: 2, alignItems: 'center', justifyContent: 'center' }}>
                <Stack direction="row" spacing={2}>
                  <StyledBadge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    variant="dot"
                  >
                    <Avatar 
                      alt={user['name']}
                      {...stringAvatar(user['name'])}
                      src={photo ? photo : ''} 
                      sx={{ width: 150, height: 150 }} 
                    />
                  </StyledBadge>
                </Stack>
              </Box>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center', justifyContent: 'center' }}>
                  <Button
                    component="label"
                    role={undefined}
                    variant="text"
                    tabIndex={-1}
                    startIcon={<AddAPhotoIcon />}
                    sx={{ textTransform: 'capitalize', display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}
                  >
                    Upload a photo
                    <VisuallyHiddenInput
                      type="file"
                      accept="image/*"
                      onChange={(event) => handleUploadPhoto(event)}
                    />
                  </Button>
                </Box>
                {/* <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, alignItems: 'center', justifyContent: 'center' }}>
                  <Tooltip title="Help">
                    <IconButton aria-label="add to favorites">
                      <HelpCenterIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Notifications">
                    <IconButton aria-label="share">
                      <NotificationsIcon />
                    </IconButton>
                  </Tooltip>
                </Box> */}
              </CardContent>
              <Divider sx={{borderWidth: 2, borderColor: localStorage.getItem('theme') === 'light' ? '#F9F9FE !important' : '#192841 !important'}} />
              <CardActions disableSpacing sx={{ justifyContent: 'center', padding: 0 }}>
                <Button variant="contained" color="primary" endIcon={<LogoutIcon />} fullWidth onClick={() => {}} style={{textTransform: 'capitalize', border: 0, borderRadius: 0, boxShadow: 'none'}}>Logout</Button>
              </CardActions>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6, xl: 6 }}>
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', maxWidth: '100%', bgcolor: 'transparent !important' }}>
              <Card sx={{ 
                width: '100%', 
                boxShadow: 'none', 
                borderRadius: 2, 
                border: '0 !important',
                bgcolor: localStorage.getItem('theme') === 'light' && 'white !important',
                filter: (theme) =>`drop-shadow(0px 0px 1px ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.32)'})` }}
              >
                <CardHeader
                  title="Account Information"
                  subheader={
                    <>
                      {"Last updated: " + (user['last_updated'] ? user['last_updated'] : 'N/A')}
                      <br />
                      {" Created at: " + (user['created_at'] ? user['created_at'] : 'N/A')}
                    </>
                  }
                  slotProps={{
                    title: {
                      component: 'span',
                      sx: {
                        fontSize: '16px',
                        fontWeight: 'bold',
                      }
                    },
                    subheader: {
                      component: 'span',
                      sx: {
                        fontSize: '12px',
                        color: 'text.secondary',
                      }
                    }
                  }}
                />
                <CardContent>
                  <List sx={{ width: '100%', maxWidth: 450, bgcolor: 'transparent !important' }}>
                    <ListItem alignItems="flex-start">
                      <ListItemText 
                        primary="Name" 
                        secondary={
                          editable ? <TextField required size="small" id="name" name="name" label={user['name']} value={name} onChange={(e) => setName(e.target.value)} variant="filled" InputProps={{
                          disableUnderline: true
                        }} 
                        sx={{width: '80%'}} autoFocus /> : name} 
                        slotProps={{
                          primary: {
                            component: 'span',
                            sx: {
                              fontSize: '14px',
                            }
                          },
                          secondary: {
                            component: 'span',
                            sx: {
                              fontSize: '12px',
                              color: 'text.secondary',
                              textAlign: 'right',
                            }
                          }
                        }}
                      />
                    </ListItem>
                    <Divider variant="inset" component="li" />
                    <ListItem alignItems="flex-start">
                      <ListItemText 
                        primary="Username" 
                        secondary={editable ? <TextField required size="small" id="username" name="username" label={user['username']} value={username} onChange={(e) => setUsername(e.target.value)} variant="filled" InputProps={{
                          disableUnderline: true
                        }} 
                        sx={{width: '80%'}} autoFocus /> : username} 
                        slotProps={{
                          primary: {
                            component: 'span',
                            sx: {
                              fontSize: '14px',
                            }
                          },
                          secondary: {
                            component: 'span',
                            sx: {
                              fontSize: '12px',
                              color: 'text.secondary',
                              textAlign: 'right',
                            }
                          }
                        }}
                      />
                    </ListItem>
                    <Divider variant="inset" component="li" />
                    <ListItem alignItems="flex-start">
                      <ListItemText 
                        primary="Company" 
                        secondary={user['company']} 
                        slotProps={{
                          primary: {
                            component: 'span',
                            sx: {
                              fontSize: '14px',
                            }
                          },
                          secondary: {
                            component: 'span',
                            sx: {
                              fontSize: '12px',
                              color: 'text.secondary',
                              textAlign: 'right',
                            }
                          }
                        }}
                      />
                    </ListItem>
                    <Divider variant="inset" component="li" />
                    <ListItem alignItems="flex-start">
                      <ListItemText 
                        primary="Role" 
                        secondary={user['role']} 
                        slotProps={{
                          primary: {
                            component: 'span',
                            sx: {
                              fontSize: '14px',
                            }
                          },
                          secondary: {
                            component: 'span',
                            sx: {
                              fontSize: '12px',
                              color: 'text.secondary',
                              textAlign: 'right',
                            }
                          }
                        }}
                      />
                    </ListItem>
                    <Divider variant="inset" component="li" />
                    <ListItem alignItems="flex-start">
                      <ListItemText 
                        primary="Phone" 
                        secondary={editable ? <TextField required size="small" id="phone" name="phone" label={user['phone']} value={phone} onChange={(e) => setPhone(e.target.value)} variant="filled" InputProps={{
                          disableUnderline: true
                        }} 
                        sx={{width: '80%'}} autoFocus /> : phone} 
                        slotProps={{
                          primary: {
                            component: 'span',
                            sx: {
                              fontSize: '14px',
                            }
                          },
                          secondary: {
                            component: 'span',
                            sx: {
                              fontSize: '12px',
                              color: 'text.secondary',
                              textAlign: 'right',
                            }
                          }
                        }}
                      />
                      </ListItem>
                    </List>
                </CardContent>
                {
                  editable && (
                    <>
                      <Divider sx={{borderWidth: 2, borderColor: localStorage.getItem('theme') !== 'light' && '#192841 !important'}} />
                      <CardActions disableSpacing sx={{ justifyContent: 'center', gap: 2 }}>
                        <Button type="submit" variant="contained" color="primary" endIcon={<SaveIcon />} fullWidth style={{textTransform: 'capitalize', border: 0, boxShadow: 'none'}}>Save</Button>
                        <Button variant="contained" color="warning" endIcon={<CancelIcon />} fullWidth onClick={() => {setEditable(false);}} style={{textTransform: 'capitalize', border: 0, boxShadow: 'none'}}>Cancel Editing</Button>
                      </CardActions>
                    </>
                  )
                }
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Box>
      {
        changePassword && (
          <Dialog
            fullWidth
            maxWidth="md"
            open={changePassword}
            onClose={() => setChangePassword(false)}
            slotProps={{
              paper: {
                component: 'form',
                onSubmit: async (event: React.SubmitEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  const formData = new FormData(event.currentTarget);
                  // Retrieve the value for the field with name="password"
                  const newPassword = formData.get('new-password') as string;
                  const currentPassword = formData.get('current-password') as string;
                  
                  if (currentPassword.length === 0) {
                    setError((prevState) => ({ ...prevState, 'current-password': 'The current password is required.' }));
                    return;
                }
                if (newPassword.length === 0) {
                    setError((prevState) => ({ ...prevState, 'new-password': 'The new password is required.' }));
                    return;
                }
                
                if (currentPassword === newPassword) {
                    setError((prevState) => ({ ...prevState, 'new-password': 'The new password cannot be the same as the current password.' }));
                    return;
                }
                
            
                if (error['current-password'] === '' && error['new-password'] === '') {
                    const res = await axios.get('/csrf');
                    axios.defaults.headers.post['xsrfCookieName'] = 'csrftoken';
                    axios.defaults.headers.post['X-CSRFToken'] = res.data.csrfToken;
                    axios.defaults.headers.post['Content-Type'] = 'application/json';
                    axios.post('/users/' + user['id'] + '/change-password/', {
                        current_password: currentPassword,
                        new_password: newPassword,
                    })
                    .then(response => {
                        if (response.data.message) {
                            toast.success(response.data.message, {position: 'top-center'});
                            setChangePassword(false);
                        }
                    })
                    .catch(error => {
                        setTimeout(() => {
                            toast.error(error.response?.data?.message, {position: 'top-center'});
                        }, 100);
                    });
                  }
                  
                  setChangePassword(false);
                },
              },
            }}
          >
            <DialogTitle><h4 style={{fontSize: '16px', fontWeight: 'bold'}}>Change Password</h4></DialogTitle>
            <DialogContent>
              <DialogContentText>
                Enter your new password here and confirm it.
              </DialogContentText>
              <TextField
                autoFocus
                required
                margin="dense"
                id="current-password"
                name="current-password"
                label="Current Password"
                type={showPassword['current-password-adornment'] ? 'text' : 'password'}
                fullWidth
                variant="standard"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment id="current-password-adornment" position="end">
                        <Tooltip title={showPassword['current-password-adornment'] ? 'Hide password' : 'Show password'}>
                            <IconButton
                                aria-label={
                                    showPassword['current-password-adornment'] ? 'Hide password' : 'Show password'
                                }
                                onClick={() => handleClickShowPassword('current-password-adornment')}
                                onMouseDown={() => handleMouseDownPassword('current-password-adornment')}
                                onMouseUp={() => handleMouseUpPassword('current-password-adornment')}
                            >
                                {showPassword['current-password-adornment'] ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </Tooltip>
                    </InputAdornment>
                    ),
                  },
                }}
              />
              {currentPassword.length > 0 ? definePasswordStrength("current-password") : <></>}
              {error['current-password'] !== '' && <FormHelperText error sx={{ mt: 1 }}>{error['current-password']}</FormHelperText>}
              
              <TextField
                autoFocus
                required
                margin="dense"
                id="new-password"
                name="new-password"
                label="New Password"
                type={showPassword['new-password-adornment'] ? 'text' : 'password'}
                fullWidth
                variant="standard"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment id="new-password-adornment" position="end">
                        <Tooltip title={showPassword['new-password-adornment'] ? 'Hide password' : 'Show password'}>
                            <IconButton
                                aria-label={
                                    showPassword['new-password-adornment'] ? 'Hide password' : 'Show password'
                                }
                                onClick={() => handleClickShowPassword('new-password-adornment')}
                                onMouseDown={() => handleMouseDownPassword('new-password-adornment')}
                                onMouseUp={() => handleMouseUpPassword('new-password-adornment')}
                            >
                                {showPassword['new-password-adornment'] ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  },
                }}
              />
              {newPassword.length > 0 ? definePasswordStrength("new-password") : <></>}
              {error['new-password'] !== '' && <FormHelperText error sx={{ mt: 1 }}>{error['new-password']}</FormHelperText>}
            </DialogContent>
            <DialogActions>
              <Button sx={{textTransform: 'capitalize', border: 0, boxShadow: 'none'}} onClick={() => setChangePassword(false)}>Cancel</Button>
              <Button type="submit" sx={{textTransform: 'capitalize', border: 0, boxShadow: 'none'}}>Change Password</Button>
            </DialogActions>
          </Dialog>
        )
      }
    </Box>
  );
}
